import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import ProductSearch from './productSearch.js';
import RegistrationEntry from './registrationEntry.js';
import PurchaseInfo from './purchaseInfo.js';
import Contact from './contact.js';
import { SIGNUP_SOURCE_IDENTITY } from '../../../../../AdobeAnalytics/code/Views/Feature/AdobeAnalytics/Constants/SignupSource.js';
import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil';
import { br_identify } from '/Foundation/Bloomreach/code/Events/Bloomreach.js';
import { br_consentOptIn } from '/Foundation/Bloomreach/code/Events/BloomreachConsent.js';
import { setEmailCookie } from '/Foundation/Common/code/TrackingCookieApi';
import { shouldPreCheckInput } from '/Foundation/Common/code/Utils/Utils.js';

function SalesForceProductRegistration() {
  const [isLoading, setIsLoading] = useState(false);
  const translations = window._THULEDATA._SalesForceProductRegistration.Texts;
  const [addedProducts, setAddedProducts] = useState({});
  const [email, setEmail] = useState();
  const [firstName, setFirstName] = useState();
  const [lastName, setLastName] = useState();
  const [phone, setPhone] = useState();

  const [retailer, setRetailer] = useState();
  const [purchaseDate, setPurchaseDate] = useState();
  const [storageApproval, setStorageApproval] = useState(false);
  const [newsLetterApproval, setNewsLetterApproval] = useState(shouldPreCheckInput());
  const [productSelectionDone, setProductSelectionDone] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const turnstileSiteKey = window._THULEDATA._SalesForceProductRegistration.TurnstileSiteKey;
  const today = new Date().toISOString().substring(0, 10);

  var turnstileId = '';

  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [restoreSearchTrigger, setRestoreSearchTrigger] = useState(0);
  const [restoreSearchResults, setRestoreSearchResults] = useState([]);

  useEffect(() => {
    if (productSelectionDone) turnstileId = window.turnstile.render('#productRegistrationTurnstile');

    return () => {
      turnstile.remove(turnstileId);
    };
  }, [productSelectionDone]);

  useEffect(() => {
    if (showErrorMessage) turnstile.reset('#productRegistrationTurnstile');
  }, [showErrorMessage]);

  const getCurrentMarket = () => {
    let scLangSplit = window._THULEDATA.SitecoreLanguage.split('-');
    if (scLangSplit.length === 2) {
      return scLangSplit[1];
    }
    return '';
  };
  const [country, setCountry] = useState(getCurrentMarket());

  const getProductKey = (product) => {
    let highestKeyObject = Object.entries(addedProducts)
      .filter(([_, value]) => value.PimId === product.PimId)
      .slice(-1)[0];

    return highestKeyObject
      ? `${product.PimId}_${parseInt(highestKeyObject[0].split('_')[1]) + 1}`
      : `${product.PimId}_${0}`;
  };

  const addProduct = (product, searchTerm) => {
    const newAddedProducts = Object.assign({}, addedProducts);
    newAddedProducts[getProductKey(product)] = product;
    setAddedProducts(newAddedProducts);
    setTimeout(() => {
      document.getElementById('serialNumber').focus();
    }, 50);
    if (searchTerm !== undefined) setLastSearchTerm(searchTerm);
  };
  const updateEntry = (product, keyName) => {
    setAddedProducts({ ...addedProducts, [keyName]: product });
  };

  const removeEntry = (keyName) => {
    if (!productSelectionDone && document.getElementById('tbProductName'))
      document.getElementById('tbProductName').focus();
    const updatedProducts = { ...addedProducts };
    delete updatedProducts[keyName];
    if (Object.keys(updatedProducts).length === 0) setProductSelectionDone(false);
    setAddedProducts(updatedProducts);
  };

  const collapseEntries = () => {
    Object.keys(addedProducts).forEach((key) => {
      const product = addedProducts[key];
      product.collapse = true;
      setAddedProducts({ ...addedProducts, [key]: product });
    });
  };

  const clearEntriesWithoutSerialnumber = () => {
    const products = { ...addedProducts };

    Object.keys(products).forEach((key) => {
      if (!products[key].serialNumber) {
        delete products[key];
      }
    });

    setAddedProducts(products);
  };

  const submitFormHandler = (event) => {
    event.preventDefault();
    saveEntry(event);
  };

  const submitProductEntryForm = (event) => {
    event.preventDefault();
    const submitter = event.nativeEvent.submitter.name;
    if (submitter === 'addMoreProducts') {
      document.getElementById('tbProductName').focus();
      collapseEntries();
    } else if (submitter === 'btnSaveProducts') {
      collapseEntries();
      setProductSelectionDone(true);
    }
  };

  const sendProductRegistration = (registrationData) => {
    const productRegistrationApiUrl = `/api/sitecore/SalesforceProductRegistration/CreateAccount`;
    const options = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(registrationData),
    };
    setShowErrorMessage(false);
    setErrorMessage(translations.ErrorMessage);

    fetch(productRegistrationApiUrl, { ...options, credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        if (!data || data.Success === 'False') {
          if (data && data.HasDuplicateError) {
            setErrorMessage(translations.DuplicateErrorMessage);
          }
          setShowErrorMessage(true);
        } else {
          setAddedProducts({});
          setProductSelectionDone(false);
          setShowSuccessMessage(true);
          setTimeout(() => {
            setShowSuccessMessage(false);
          }, 5000);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        setRestoreSearchResults([]);
        setRestoreSearchTrigger(0);
        setLastSearchTerm('');
      })
      .catch((e) => {
        setShowErrorMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const saveEntry = (event) => {
    if (isLoading) return;
    setIsLoading(true);

    // Set email cookie for tracking
    if (email) {
      setEmailCookie(email);
    }

    const accountRequest = {
      FirstName: firstName,
      LastName: lastName,
      PersonEmail: email,
      Phone: phone,
      BillingCountryCode: country,
      ShippingCountryCode: country,
      UserLanguage: window._THULEDATA.SitecoreLanguage,
      DataStorageConsent: storageApproval,
    };
    let productRegistrationRequests = [];
    Object.keys(addedProducts).forEach((key) => {
      const product = addedProducts[key];
      const request = {
        DateOfPurchase: purchaseDate,
        PimId: product.PimId,
        PlaceOfPurchase: retailer,
        Product: product.Name,
        Name: product.Name,
        SerialNumber: product.serialNumber,
        KeyNumber: product.keyNumber,
      };
      productRegistrationRequests.push(request);
    });

    const req = {
      AccountRequest: accountRequest,
      ProductRegistrationRequests: productRegistrationRequests,
      TurnstileToken: document.querySelector('#productRegistrationTurnstile input[name=cf-turnstile-response]').value,
      SettingsId: window._THULEDATA._SalesForceProductRegistration.SettingsId,
    };
    sendProductRegistration(req);

    if (newsLetterApproval) {
      br_consentOptIn(email);
      AdobeAnalyticsUtil.DataLayer.TrackEvent('newsletter_signup', {
        newsletter: { variant: SIGNUP_SOURCE_IDENTITY.PRODUCT_REGISTRATION },
      });
    } else {
      br_identify(email);
    }
  };

  const handleProductAdd = (searchTerm) => {
    setLastSearchTerm(searchTerm);
  };

  const handleChangeEntry = (keyName) => {
    removeEntry(keyName);
    setRestoreSearchTrigger((prev) => prev + 1);
  };

  return (
    <div id="salesForceProductRegistration" className="row">
      {!showSuccessMessage ? (
        <div className="col-12 col-lg-6 offset-lg-3 mb-5">
          <h1 className="heading3">{translations.RegisterNewProduct}</h1>
          <div className="mt-3" dangerouslySetInnerHTML={{ __html: translations.Description }} />
          {!productSelectionDone && (
            <ProductSearch
              translations={translations}
              addProduct={(product) => addProduct(product, lastSearchTerm)}
              clearEntriesWithoutSerialnumber={clearEntriesWithoutSerialnumber}
              onProductAdd={handleProductAdd}
              restoreSearchTerm={lastSearchTerm}
              restoreSearchTrigger={restoreSearchTrigger}
              restoreSearchResults={restoreSearchResults}
              setRestoreSearchResults={setRestoreSearchResults}
            />
          )}

          <form
            autoComplete="off"
            onSubmit={(e) => {
              submitProductEntryForm(e);
            }}
          >
            {Object.keys(addedProducts).map(function (keyName, keyIndex) {
              return (
                <RegistrationEntry
                  key={keyName}
                  removeEntry={removeEntry}
                  updateEntry={updateEntry}
                  product={addedProducts[keyName]}
                  translations={translations}
                  keyName={keyName}
                  onChangeEntry={handleChangeEntry}
                />
              );
            })}
            {Object.keys(addedProducts).length > 0 && !productSelectionDone && (
              <div>
                <button type="submit" name="addMoreProducts" className="sfpr__addMoreProducts">
                  <span>{translations.AddMoreProducts} </span>
                  <span className="frg-icon-Plus-1"></span>
                </button>
              </div>
            )}

            {Object.keys(addedProducts).length > 0 && !productSelectionDone && (
              <div>
                <input
                  name="btnSaveProducts"
                  className="btn thule-btn-default w-100"
                  type="submit"
                  id="btnSaveProducts"
                  value={translations.Save}
                />
              </div>
            )}
          </form>

          {Object.keys(addedProducts).length > 0 && (
            <form
              onSubmit={(e) => {
                submitFormHandler(e);
              }}
            >
              <fieldset disabled={Object.keys(addedProducts).length === 0 || !productSelectionDone}>
                <PurchaseInfo
                  translations={translations}
                  country={country}
                  setCountry={setCountry}
                  setRetailer={setRetailer}
                  setPurchaseDate={setPurchaseDate}
                  today={today}
                />
                <Contact
                  translations={translations}
                  setEmail={setEmail}
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                  setPhone={setPhone}
                />
                {window._THULEDATA._SalesForceProductRegistration.IsNewsletterSignupEnabled && (
                  <>
                    <div className="d-flex mb-3">
                      <div className="sfpr__inputField">
                        <input
                          type="checkbox"
                          id="receiveNewsLetter"
                          checked={newsLetterApproval}
                          onChange={(e) => setNewsLetterApproval(e.target.checked)}
                        />
                      </div>
                      <div className="ms-4">
                        <label className="d-inline fw-bold" htmlFor="receiveNewsLetter">
                          {translations.ReceiveNewsletterTitle}
                        </label>
                        <div
                          className="ms-2 d-inline"
                          dangerouslySetInnerHTML={{ __html: translations.ReceiveNewsletterText }}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="d-flex mb-3">
                  <div className="sfpr__inputField">
                    <input
                      required
                      type="checkbox"
                      id="storageApproval"
                      onChange={(e) => setStorageApproval(e.target.value)}
                    />
                  </div>
                  <div className="ms-4">
                    <label className="d-inline fw-bold" htmlFor="storageApproval">
                      {translations.DataStorageTitle}
                    </label>
                    <div className="ms-2 d-inline" dangerouslySetInnerHTML={{ __html: translations.DataStorageText }} />
                  </div>
                </div>
                <div
                  id="productRegistrationTurnstile"
                  className="cf-turnstile"
                  data-sitekey={turnstileSiteKey}
                  data-theme="light"
                ></div>
                {showErrorMessage && (
                  <div className="errorMessage" dangerouslySetInnerHTML={{ __html: errorMessage }} />
                )}
                <div>
                  <input
                    className="sfpr__complete btn thule-btn-default w-100"
                    type="submit"
                    disabled={new Date(purchaseDate) > new Date(today)}
                    value={translations.RegisterButtonText}
                  />
                </div>
              </fieldset>
            </form>
          )}
        </div>
      ) : (
        <div className="col-12 col-lg-6 offset-lg-3 mb-5 text-center successMessage">
          <div className="d-flex justify-content-center">
            <span className="checkIcon">
              <i className="fas fa-check"></i>
            </span>
          </div>
          <span className="heading3 d-block mb-5">{translations.SuccessTitle}</span>
          <div>{translations.SuccessDescription}</div>
          <div className="mt-5 d-flex justify-content-center">
            <button onClick={(e) => setShowSuccessMessage(false)} className="btn fw-bold d-flex align-items-center">
              <span>{translations.BackToProductRegistration}</span>
              <span className="ms-2 arrow frg-icon-Arrow-long"></span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const root = createRoot(document.getElementById('REACT_salesForceProductRegistration'));
root.render(
  <Sentry.ErrorBoundary>
    <SalesForceProductRegistration />
  </Sentry.ErrorBoundary>
);
